// =============================================
// PromptEditor — panel "Prompty AI (pipeline)"
//
// Od migracji prompts_no_versioning (2026-08-23): 1 wiersz = 1 step, ZERO UI wersji.
// Edycja in-place; updated_at bumpuje trigger DB.
//
// MODELE — dwie kolumny CELOWO:
//   * `model_id` — źródło prawdy dla content-engine (loadPrompt().modelId),
//   * `model`    — czytają legacy konsumenci: sbdGenerator, scnAuditor,
//                  autoGenerateMapRunner, cała ścieżka map-pipeline
//                  (mapGenerationCore→MODEL_IDS) oraz worker (priorytet model→model_id).
//   Dlatego zapis modelu USTAWIA OBIE kolumny — bez tego część pipeline'u
//   nie zauważyłaby zmiany.
//
// `recommended_model` — snapshot efektywnego modelu runtime z 2026-08-23
// (na nim wygenerowano ~3000 artykułów). Odjazd model_id od snapshotu =
// ostrzeżenie + przycisk "Przywróć rekomendowany".
//
// eav_extraction ma świadomie model≠model_id (2 żywe ścieżki, 2 modele) —
// ręczna zmiana modelu w panelu unifikuje obie kolumny po jawnym confirm.
// =============================================
import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Save,
  Download,
  Map as MapIcon,
  Brain,
  ShieldCheck,
  Boxes,
  FileText,
  Search,
  Archive,
  UserCircle,
  Newspaper,
  Maximize2,
  RotateCcw,
  AlertTriangle,
  GitFork,
  Cpu,
} from "lucide-react";
import { useThematicPrompts } from "@/hooks/useThematicPrompts";
import { supabase } from "@/integrations/supabase/client";
import type { PromptStep, PromptModel, ThematicPrompt } from "@/types/thematicMaps";
import { PROMPT_MODELS } from "@/types/thematicMaps";
import { findUnknownVars } from "@/lib/promptVars";

const STEP_LABELS: Record<PromptStep, string> = {
  macro_generation: "Macro Generation",
  seed_generation: "Seed Generation",
  node_generation: "Node Generation",
  entity_extraction: "Entity Extraction",
  eav_extraction: "EAV Extraction",
  deduplication: "Deduplication",
  linking: "Linking (Relacje)",
  content_plan: "Plan treści",
  content_full: "Pełna treść",
  sbd_generation: "Semantic Boundary Declaration",
  scn_audit: "SCN Audit v2 (SRO 5 warstw)",
  kg_assembly: "Knowledge Graph Assembly",
  fan_out: "Fan-Out: Intent & Areas",
  fan_out_paa_assignment: "Fan-Out: PAA Assignment",
  fan_out_classification: "Fan-Out: MICRO/MACRO Classification",
  cluster_dedup: "Cluster Dedup (LLM)",
  area_consolidation: "Area Consolidation (pre-outline)",
  writing_profile_generation: "Auto Writing Profile Generator",
  intro_generation: "Draft — Intro",
  section_generation: "Draft — Section H2",
  outline_generation: "Outline + Distribution",
  draft_generation: "Draft (Block-by-Block)",
  enrichment: "Data Enrichment",
  optimization: "Optimization (2-pass)",
  optimization_intermediate: "Optimization Intermediate (G,H,K)",
  humanization: "Humanization (4-tier)",
  wp_sro_extraction: "SRO Extraction z wpisów WP",
  wp_matching: "SRO Matching node vs wpis WP",
  wp_quality_audit: "Quality Audit wpisów WP (SRO-based)",
  wp_cluster_fit: "Cluster Fit Classifier (scope: in/adjacent/off)",
  seo_meta_generation: "SEO Meta (title + description) — przed publikacją",
  content_audit_llm: "Content Audit LLM (Layer 6 — po publikacji)",
  standalone_style_from_description: "Standalone — Style z opisu",
  standalone_style_from_samples: "Standalone — Style z sampli",
  section_generation_standalone: "Standalone — Sekcja H2 (KLON-A, anti-cross-ref)",
  image_prompt_generation: "Image Prompt Generation (FAL.ai)",
  entity_extraction_serp: "Entity Extraction z SERP (NER)",
  data_extraction: "Data Extraction (SERP → dane)",
  enrichment_verify: "Enrichment — weryfikacja claimów",
  link_weaving: "Link Weaving — wplatanie linków wewnętrznych",
  outline_generation_pillar: "Pillar — Outline",
  section_generation_pillar: "Pillar — Sekcje H2",
  intro_generation_pillar: "Pillar — Intro",
  author_page_generation: "Strona autora",
  unique_subpage_topics: "Podstrony — propozycje tematów",
  unique_subpage_generation: "Podstrony — treść HTML",
  author_photo_prompt: "Zdjęcie autora — prompt obrazkowy (Seedream 4.5)",
  brand_logo_prompt: "Logo i favicon — prompt wektorowy (Recraft V4.1)",
} as Record<PromptStep, string>;

interface StageGroup {
  title: string;
  description: string;
  icon: typeof MapIcon;
  steps: PromptStep[];
}

const STAGE_GROUPS: StageGroup[] = [
  {
    title: "1. Strategia mapy (Claude)",
    description: "Macro, granice tematyczne (SBD), klastry (seeds) i konkretne nody.",
    icon: MapIcon,
    steps: ["macro_generation", "sbd_generation", "seed_generation", "node_generation"],
  },
  {
    title: "2. Wiedza (Claude)",
    description: "Encje, atrybuty (EAV), relacje między nodami, dedup semantyczny.",
    icon: Brain,
    steps: ["entity_extraction", "eav_extraction", "linking", "deduplication"],
  },
  {
    title: "3. Audyt SCN (Claude)",
    description: "4-wymiarowy audyt zgodności (boundary / topology / links / corridors).",
    icon: ShieldCheck,
    steps: ["scn_audit"],
  },
  {
    title: "4. Content Engine — Struktura (OpenAI)",
    description: "SERP NER + dane, Knowledge Graph, Fan-Out, klasyfikacja, outline + profil pisarski.",
    icon: Boxes,
    steps: [
      "entity_extraction_serp",
      "data_extraction",
      "kg_assembly",
      "fan_out",
      "fan_out_classification",
      "fan_out_paa_assignment",
      "cluster_dedup",
      "area_consolidation",
      "outline_generation",
      "writing_profile_generation",
    ],
  },
  {
    title: "5. Content Engine — Treść (OpenAI)",
    description: "Intro + sekcje H2, draft, enrichment + weryfikacja, optymalizacja, humanizacja.",
    icon: FileText,
    steps: [
      "intro_generation",
      "section_generation",
      "draft_generation",
      "enrichment",
      "enrichment_verify",
      "link_weaving",
      "optimization",
      "optimization_intermediate",
      "humanization",
    ],
  },
  {
    title: "6. Pillar & Author (SRO, OpenAI)",
    description: "Pillar Page (hub klastra, dedykowany flow) + Strona autora (ProfilePage schema).",
    icon: Boxes,
    steps: [
      "outline_generation_pillar",
      "intro_generation_pillar",
      "section_generation_pillar",
      "author_page_generation",
    ],
  },
  {
    title: "7. Istniejące wpisy / SRO (OpenAI)",
    description: "Ekstrakcja SRO z wpisów WP, audyt jakości LLM, matching semantyczny node ↔ wpis.",
    icon: Search,
    steps: ["wp_sro_extraction", "wp_quality_audit", "wp_cluster_fit", "wp_matching"],
  },
  {
    title: "8. Publikacja & QA (OpenAI)",
    description: "SEO meta + prompt obrazka wyróżniającego przed publikacją, audyt Layer 6 po publikacji.",
    icon: ShieldCheck,
    steps: ["seo_meta_generation", "image_prompt_generation", "content_audit_llm"],
  },
  {
    title: "9. Standalone — Artykuły",
    description: "Tryb pojedynczych artykułów: styl z opisu/sampli + klon sekcji H2 bez cross-ref.",
    icon: Newspaper,
    steps: [
      "standalone_style_from_description",
      "standalone_style_from_samples",
      "section_generation_standalone",
    ],
  },
  {
    title: "10. Ulepszanie zaplecza (podstrony + autor)",
    description:
      "Unikalne podstrony serwisu (O nas / Metodyka), zdjęcie autora (Seedream 4.5), logo i favicon (Recraft V4.1).",
    icon: UserCircle,
    steps: ["unique_subpage_topics", "unique_subpage_generation", "author_photo_prompt", "brand_logo_prompt"],
  },
  {
    title: "Legacy (nieużywane w pipeline)",
    description: "Stare prompty zachowane dla kompatybilności. Nie dotykaj bez potrzeby.",
    icon: Archive,
    steps: ["content_plan", "content_full"],
  },
];

/** Stepy przetwarzane też przez worker mode — worker akceptuje wyłącznie modele Claude. */
const WORKER_MODE_STEPS: ReadonlySet<string> = new Set([
  "seed_generation",
  "node_generation",
  "entity_extraction",
  "eav_extraction",
  "linking",
]);

const providerOf = (model: string): "claude" | "openai" =>
  model?.startsWith("claude") ? "claude" : "openai";

const relativeTime = (iso?: string | null): string => {
  if (!iso) return "—";
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(diffMin)) return "—";
  if (diffMin < 1) return "przed chwilą";
  if (diffMin < 60) return `${diffMin} min temu`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} h temu`;
  const d = Math.floor(h / 24);
  if (d < 60) return `${d} dni temu`;
  return new Date(iso).toISOString().slice(0, 10);
};

async function exportAllPrompts() {
  const { data, error } = await (supabase as any)
    .from("thematic_prompts")
    .select("step, prompt_template, model, provider, model_id, recommended_model, temperature, updated_at")
    .order("step", { ascending: true });

  if (error || !data) {
    alert("Błąd pobierania promptów: " + (error?.message || "brak danych"));
    return;
  }

  const exportData = {
    exported_at: new Date().toISOString(),
    total: data.length,
    prompts: data.map((p: any) => ({
      step: p.step,
      label: STEP_LABELS[p.step as PromptStep] || p.step,
      model: p.model,
      provider: p.provider || "claude",
      model_id: p.model_id || null,
      recommended_model: p.recommended_model || null,
      temperature: p.temperature ?? null,
      updated_at: p.updated_at,
      prompt_template: p.prompt_template,
    })),
  };

  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `prompts-export-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

interface PendingModelChange {
  prompt: ThematicPrompt;
  newModel: PromptModel;
  crossOperator: boolean;
  unifiesConflict: boolean;
}

export function PromptEditor() {
  const { prompts, loading, updatePrompt } = useThematicPrompts();
  const [editedTemplates, setEditedTemplates] = useState<Record<string, string>>({});
  const [editedModels, setEditedModels] = useState<Record<string, PromptModel>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fullscreenId, setFullscreenId] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingModelChange | null>(null);
  // Dirty-guard przy przełączaniu akordeonu (type="single" zwija poprzedni)
  const [openByGroup, setOpenByGroup] = useState<Record<string, string>>({});
  const [pendingCollapse, setPendingCollapse] = useState<{ groupTitle: string; nextValue: string } | null>(null);

  const hasDirty = Object.keys(editedTemplates).length > 0 || Object.keys(editedModels).length > 0;

  // Ostrzeżenie przy zamknięciu karty/przeładowaniu, gdy są niezapisane zmiany
  useEffect(() => {
    if (!hasDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasDirty]);

  // Koszyk "Pozostałe" — stepy obecne w DB, których nie ma w żadnej grupie.
  // Gwarancja: nowy prompt w DB NIGDY nie jest niewidzialny w panelu.
  const ungroupedSteps = useMemo(() => {
    const grouped = new Set<string>(STAGE_GROUPS.flatMap((g) => g.steps));
    return prompts.map((p) => p.step).filter((s) => !grouped.has(s));
  }, [prompts]);

  const allGroups: StageGroup[] = useMemo(
    () =>
      ungroupedSteps.length > 0
        ? [
            ...STAGE_GROUPS,
            {
              title: "Pozostałe (spoza grup)",
              description: "Stepy obecne w bazie, nieprzypisane do żadnej fazy — przypisz je w STAGE_GROUPS.",
              icon: Boxes,
              steps: ungroupedSteps as PromptStep[],
            },
          ]
        : STAGE_GROUPS,
    [ungroupedSteps],
  );

  const matchesSearch = (step: PromptStep): boolean => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return step.toLowerCase().includes(q) || (STEP_LABELS[step] || "").toLowerCase().includes(q);
  };

  const discardEditsFor = (id: string) => {
    setEditedTemplates((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setEditedModels((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = async (prompt: ThematicPrompt) => {
    const id = prompt.id;
    const updates: { prompt_template?: string; model?: PromptModel; model_id?: string } = {};

    if (editedTemplates[id] !== undefined) {
      updates.prompt_template = editedTemplates[id];
    }
    if (editedModels[id] !== undefined) {
      // KLUCZOWE: oba pola naraz — patrz komentarz na górze pliku (legacy konsumenci `model`).
      updates.model = editedModels[id];
      updates.model_id = editedModels[id];
    }

    if (Object.keys(updates).length === 0) return;

    setSavingId(id);
    const ok = await updatePrompt(id, updates);
    if (ok) discardEditsFor(id);
    setSavingId(null);
  };

  /** Zmiana modelu w select — confirm przy zmianie operatora i/lub unifikacji konfliktu. */
  const requestModelChange = (prompt: ThematicPrompt, newModel: PromptModel) => {
    const current = (editedModels[prompt.id] ?? prompt.model_id ?? prompt.model) as string;
    if (newModel === current) return;
    const isConflictRow = !!prompt.model_id && prompt.model !== prompt.model_id;
    const crossOperator = providerOf(newModel) !== providerOf(current);
    if (crossOperator || isConflictRow) {
      setPendingChange({ prompt, newModel, crossOperator, unifiesConflict: isConflictRow });
    } else {
      setEditedModels((prev) => ({ ...prev, [prompt.id]: newModel }));
    }
  };

  const applyPendingChange = () => {
    if (!pendingChange) return;
    setEditedModels((prev) => ({ ...prev, [pendingChange.prompt.id]: pendingChange.newModel }));
    setPendingChange(null);
  };

  /** "Przywróć rekomendowany" — przez ten sam flow (dla eav pokaże confirm unifikacji). */
  const restoreRecommended = (prompt: ThematicPrompt) => {
    if (!prompt.recommended_model) return;
    requestModelChange(prompt, prompt.recommended_model as PromptModel);
  };

  const handleAccordionChange = (groupTitle: string, nextValue: string) => {
    const currentOpen = openByGroup[groupTitle];
    if (currentOpen) {
      const openPrompt = prompts.find((p) => p.step === currentOpen);
      const dirty =
        openPrompt &&
        (editedTemplates[openPrompt.id] !== undefined || editedModels[openPrompt.id] !== undefined);
      if (dirty) {
        setPendingCollapse({ groupTitle, nextValue });
        return;
      }
    }
    setOpenByGroup((prev) => ({ ...prev, [groupTitle]: nextValue }));
  };

  const confirmCollapse = () => {
    if (!pendingCollapse) return;
    const openStep = openByGroup[pendingCollapse.groupTitle];
    const openPrompt = prompts.find((p) => p.step === openStep);
    if (openPrompt) discardEditsFor(openPrompt.id);
    setOpenByGroup((prev) => ({ ...prev, [pendingCollapse.groupTitle]: pendingCollapse.nextValue }));
    setPendingCollapse(null);
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground py-4">Ładowanie promptów...</p>;
  }

  const fullscreenPrompt = fullscreenId ? prompts.find((p) => p.id === fullscreenId) : null;

  const renderStepAccordionItem = (step: PromptStep) => {
    const prompt = prompts.find((p) => p.step === step);
    if (!prompt) {
      return (
        <AccordionItem key={step} value={step} className="border rounded-lg px-4 opacity-60">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{STEP_LABELS[step] || step}</span>
              <Badge variant="outline" className="text-xs text-muted-foreground">
                brak promptu
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-muted-foreground mb-3">
              Prompt dla tego etapu nie istnieje w bazie. Dodaj go migracją SQL (INSERT z krokiem z CHECK-a).
            </p>
          </AccordionContent>
        </AccordionItem>
      );
    }

    const currentTemplate = editedTemplates[prompt.id] ?? prompt.prompt_template;
    // model_id = źródło prawdy content-engine; `model` tylko dla legacy konsumentów
    const effectiveModel = (prompt.model_id || prompt.model) as PromptModel;
    const currentModel = (editedModels[prompt.id] ?? effectiveModel) as PromptModel;
    const isTemplateEdited = editedTemplates[prompt.id] !== undefined;
    const isModelEdited = editedModels[prompt.id] !== undefined;
    const isEdited = isTemplateEdited || isModelEdited;

    const modelLabel = PROMPT_MODELS.find((m) => m.value === currentModel)?.label || currentModel;
    const provider = providerOf(currentModel);
    const isConflictRow = !!prompt.model_id && prompt.model !== prompt.model_id;
    const offRecommended =
      !!prompt.recommended_model && (editedModels[prompt.id] ?? prompt.model_id) !== prompt.recommended_model;
    const unknownVars = findUnknownVars(currentTemplate);

    return (
      <AccordionItem key={step} value={step} className="border rounded-lg px-4">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{STEP_LABELS[step] || step}</span>
            <Badge variant="outline" className="text-xs flex items-center gap-1">
              <Cpu className="h-3 w-3" />
              {modelLabel}
            </Badge>
            <Badge
              variant="outline"
              className={
                provider === "claude"
                  ? "text-[10px] uppercase border-orange-500/40 text-orange-400"
                  : "text-[10px] uppercase border-emerald-500/40 text-emerald-400"
              }
            >
              {provider}
            </Badge>
            {isConflictRow && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-[10px] border-purple-500/40 text-purple-400 flex items-center gap-1 cursor-default"
                    >
                      <GitFork className="h-3 w-3" />2 ścieżki, 2 modele
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[320px]">
                    <p className="text-xs">
                      map-pipeline/worker używa <span className="font-mono">{prompt.model}</span>, content-engine{" "}
                      <span className="font-mono">{prompt.model_id}</span>. Zapis modelu z panelu ujednolici obie
                      ścieżki (z potwierdzeniem).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {offRecommended && (
              <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/40 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                inny niż rekomendowany
              </Badge>
            )}
            {isEdited && <Badge className="text-xs bg-amber-100 text-amber-700">niezapisane</Badge>}
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-2 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-muted-foreground font-mono">
            <span>
              step: <span className="text-foreground">{step}</span>
            </span>
            {prompt.temperature !== null && prompt.temperature !== undefined && (
              <span>
                temp: <span className="text-foreground">{prompt.temperature}</span>
              </span>
            )}
            <span>
              chars: <span className="text-foreground">{currentTemplate.length.toLocaleString("pl-PL")}</span>
            </span>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    Zmieniono: <span className="text-foreground">{relativeTime(prompt.updated_at)}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs font-mono">{prompt.updated_at ? new Date(prompt.updated_at).toLocaleString("pl-PL") : "—"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {WORKER_MODE_STEPS.has(step) && (
            <p className="mb-2 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 text-amber-400 shrink-0" />
              Worker mode: modele gpt-* nie zadziałają — worker degraduje je do Claude (sonnet-4-6).
            </p>
          )}

          <div className="mb-3 flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Model AI</label>
              <Select value={currentModel} onValueChange={(val) => requestModelChange(prompt, val as PromptModel)}>
                <SelectTrigger className="w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>OpenAI</SelectLabel>
                    {PROMPT_MODELS.filter((m) => providerOf(m.value) === "openai").map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Claude</SelectLabel>
                    {PROMPT_MODELS.filter((m) => providerOf(m.value) === "claude").map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {prompt.recommended_model && (
              <div className="flex items-center gap-2 pb-0.5">
                <Badge
                  variant="outline"
                  className={
                    offRecommended
                      ? "text-xs border-amber-500/50 text-amber-400 bg-amber-500/10"
                      : "text-xs border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                  }
                >
                  Rekomendowany: {prompt.recommended_model}
                </Badge>
                {offRecommended && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => restoreRecommended(prompt)}
                    title="Ustawia model i model_id na snapshot sprawdzonej konfiguracji"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Przywróć rekomendowany
                  </Button>
                )}
              </div>
            )}
          </div>

          {unknownVars.length > 0 && (
            <div className="mb-2 flex items-center gap-1.5 flex-wrap text-[11px]">
              <span className="text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Nieznane zmienne (możliwa literówka — zapis NIE jest blokowany):
              </span>
              {unknownVars.map((v) => (
                <Badge key={v} variant="outline" className="font-mono text-[10px] border-amber-500/50 text-amber-400">
                  {"{{" + v + "}}"}
                </Badge>
              ))}
            </div>
          )}

          <div className="relative">
            <Textarea
              value={currentTemplate}
              onChange={(e) =>
                setEditedTemplates((prev) => ({
                  ...prev,
                  [prompt.id]: e.target.value,
                }))
              }
              rows={24}
              className="font-mono text-xs"
              spellCheck={false}
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-7 w-7 p-0 bg-background/80"
              onClick={() => setFullscreenId(prompt.id)}
              title="Edytor pełnoekranowy"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-muted-foreground font-mono">
              {currentTemplate.length.toLocaleString("pl-PL")} znaków
            </span>
            <Button size="sm" onClick={() => handleSave(prompt)} disabled={!isEdited || savingId === prompt.id}>
              <Save className="mr-1 h-3.5 w-3.5" />
              {savingId === prompt.id ? "Zapisywanie..." : "Zapisz prompt"}
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj stepu... (np. outline, pillar, humanization)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        {hasDirty && (
          <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/40">
            niezapisane zmiany
          </Badge>
        )}
        <Button variant="outline" size="sm" onClick={exportAllPrompts}>
          <Download className="mr-1 h-3.5 w-3.5" />
          Eksport (JSON)
        </Button>
      </div>

      {allGroups.map((group) => {
        const Icon = group.icon;
        const visibleSteps = group.steps.filter(matchesSearch);
        if (visibleSteps.length === 0) return null;
        const presentCount = group.steps.filter((s) => prompts.some((p) => p.step === s)).length;
        return (
          <section key={group.title} className="space-y-2">
            <div className="flex items-start gap-2 border-b pb-2 sticky top-0 z-10 bg-background/95 backdrop-blur pt-1">
              <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold">{group.title}</h3>
                  <Badge variant="secondary" className="text-[10px]">
                    {presentCount}/{group.steps.length} promptów
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{group.description}</p>
              </div>
            </div>
            <Accordion
              type="single"
              collapsible
              className="space-y-2"
              value={openByGroup[group.title] ?? ""}
              onValueChange={(v) => handleAccordionChange(group.title, v)}
            >
              {visibleSteps.map((step) => renderStepAccordionItem(step))}
            </Accordion>
          </section>
        );
      })}

      {/* Edytor pełnoekranowy */}
      <Dialog open={!!fullscreenPrompt} onOpenChange={(open) => !open && setFullscreenId(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col">
          {fullscreenPrompt && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm">
                  {STEP_LABELS[fullscreenPrompt.step] || fullscreenPrompt.step}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">({fullscreenPrompt.step})</span>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {(editedTemplates[fullscreenPrompt.id] ?? fullscreenPrompt.prompt_template).length.toLocaleString(
                    "pl-PL",
                  )}{" "}
                  znaków · zmiany zapisujesz przyciskiem na karcie stepu
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={editedTemplates[fullscreenPrompt.id] ?? fullscreenPrompt.prompt_template}
                onChange={(e) =>
                  setEditedTemplates((prev) => ({
                    ...prev,
                    [fullscreenPrompt.id]: e.target.value,
                  }))
                }
                className="font-mono text-xs flex-1 resize-none"
                spellCheck={false}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setFullscreenId(null)}>
                  Zamknij
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    handleSave(fullscreenPrompt);
                    setFullscreenId(null);
                  }}
                  disabled={
                    editedTemplates[fullscreenPrompt.id] === undefined &&
                    editedModels[fullscreenPrompt.id] === undefined
                  }
                >
                  <Save className="mr-1 h-3.5 w-3.5" />
                  Zapisz prompt
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm: zmiana operatora / unifikacja konfliktu */}
      <AlertDialog open={!!pendingChange} onOpenChange={(open) => !open && setPendingChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Potwierdź zmianę modelu
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {pendingChange?.crossOperator && (
                  <p>
                    Prompt stepu{" "}
                    <span className="font-mono">{pendingChange.prompt.step}</span> jest dopasowany pod{" "}
                    <strong>
                      {providerOf(
                        (editedModels[pendingChange.prompt.id] ??
                          pendingChange.prompt.model_id ??
                          pendingChange.prompt.model) as string,
                      ) === "claude"
                        ? "Claude"
                        : "OpenAI"}
                    </strong>{" "}
                    — zmiana operatora może pogorszyć wyniki.
                  </p>
                )}
                {pendingChange?.unifiesConflict && (
                  <p>
                    Ten step ma dziś <strong>2 ścieżki i 2 modele</strong>: map-pipeline/worker →{" "}
                    <span className="font-mono">{pendingChange.prompt.model}</span>, content-engine →{" "}
                    <span className="font-mono">{pendingChange.prompt.model_id}</span>. Zapis ustawi{" "}
                    <strong>OBIE</strong> ścieżki na{" "}
                    <span className="font-mono">{pendingChange.newModel}</span> — to świadoma unifikacja.
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  Zmiana wejdzie w życie dopiero po kliknięciu „Zapisz prompt".
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={applyPendingChange}>Zmień model</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: zwinięcie karty z niezapisanymi zmianami */}
      <AlertDialog open={!!pendingCollapse} onOpenChange={(open) => !open && setPendingCollapse(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Porzucić niezapisane zmiany?</AlertDialogTitle>
            <AlertDialogDescription>
              Otwarty prompt ma niezapisane zmiany. Zwinięcie karty je odrzuci.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Wróć do edycji</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCollapse}>Porzuć zmiany</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
